import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Observable, catchError, finalize, map, of, shareReplay } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { StaffModuleSlug, StaffPermissionSlug } from './permission.constants';

interface StaffPermissionAction {
  id: string;
  action: string;
  type: string;
  slugCode: string;
}

interface StaffPermissionModule {
  moduleId: string;
  moduleName: string;
  slugCode: string;
  moduleIndex: number;
  isEnabled: boolean;
  actions: StaffPermissionAction[];
}

interface StaffPermissionsResponse {
  success: boolean;
  message: string;
  data: {
    userId: string;
    modules: StaffPermissionModule[];
  };
}

/** Message the endpoint returns while the staff user still has access. */
const PERMISSIONS_GRANTED_MESSAGE = 'Staff permissions retrieved';

/**
 * Module access follows `modules[].isEnabled`; action grants follow
 * `modules[].actions[].slugCode` on enabled modules.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly permissionSlugs = signal<ReadonlySet<string>>(new Set());
  private readonly moduleSlugs = signal<ReadonlySet<string>>(new Set());
  private readonly message = signal<string>('');
  private loadedForToken: string | null = null;
  private loadInFlight$: Observable<boolean> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
  ) {}

  hasPermission(slugCode: StaffPermissionSlug): boolean {
    return this.permissionSlugs().has(slugCode);
  }

  hasModule(slugCode: StaffModuleSlug): boolean {
    return this.moduleSlugs().has(slugCode);
  }

  /** False when the endpoint replies with a notice instead of the granted message. */
  hasAccess(): boolean {
    return this.message().trim() === PERMISSIONS_GRANTED_MESSAGE;
  }

  /** Message returned by the permissions endpoint (e.g. the no-access notice). */
  accessMessage(): string {
    return this.message();
  }

  /** Fetches once per access token; shares one in-flight request across guards. */
  load(): Observable<boolean> {
    const token = this.authService.getAccessToken();
    if (!token) {
      this.reset();
      return of(false);
    }

    if (this.loadedForToken === token) {
      return of(true);
    }

    if (this.loadInFlight$) {
      return this.loadInFlight$;
    }

    this.permissionSlugs.set(new Set());
    this.moduleSlugs.set(new Set());
    this.message.set('');
    this.loadInFlight$ = this.http
      .get<StaffPermissionsResponse>(`${environment.apiUrl}/staff-auth/permissions`)
      .pipe(
        map((response) => {
          if (!response.success) {
            this.reset();
            return false;
          }
          this.message.set(response.message ?? '');
          const enabledModules = response.data.modules.filter(
            (module) => module.isEnabled,
          );
          this.moduleSlugs.set(
            new Set(enabledModules.map((module) => module.slugCode)),
          );
          const slugs = enabledModules.flatMap((module) =>
            module.actions.map((action) => action.slugCode),
          );
          this.permissionSlugs.set(new Set(slugs));
          this.loadedForToken = token;
          return true;
        }),
        // Fail closed: an unreadable grant list hides modules and actions.
        catchError(() => {
          this.reset();
          return of(false);
        }),
        finalize(() => {
          this.loadInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    return this.loadInFlight$;
  }

  reset(): void {
    this.permissionSlugs.set(new Set());
    this.moduleSlugs.set(new Set());
    this.message.set('');
    this.loadedForToken = null;
  }
}
